import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import { useDatabase } from '../../context/DatabaseContext';
import { useAuth } from '../../context/AuthContext';
import { doc, updateDoc, addDoc, deleteDoc, writeBatch, collection } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { motion, AnimatePresence } from 'framer-motion';

const ITEMS_PER_PAGE = 10;

const EMPTY_MEMBER = {
  full_name: '',
  full_name_sindhi: '',
  literary_name: '',
  dob: '',
  literary_services: '',
  districtId: '',
  literary_genre: [],
  contact_info: {
    phone: '',
    email: '',
  },
  addresses: {
    permanent: '',
    temporary: '',
  },
  current_branch_id: '',
  status: 'pending',
  membership_status: '',
  join_date: '',
};

export default function ManageMembers() {
  const t = useT();
  const isRTL = t.language === 'sindhi';
  const { getCollection, db } = useDatabase();
  
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [branches, setBranches] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [literaryFields, setLiteraryFields] = useState([]);
  
  const { user } = useAuth();
  const isGlobalAdmin = user?.role === 'main_admin' || user?.role?.startsWith('center_body_');
  const isBranchAdmin = user?.role === 'branch_admin' || user?.role === 'branch_secretary_general';
  
  // Non-global admins (including Branch Auditor, Treasurer, Members, etc.) only see their branch
  const isRestrictedToBranch = !isGlobalAdmin;
  // Only Branch Admins/Secretaries can edit/delete. Main Admin is Read-Only here.
  const isMemberOnly = user?.role === 'member';
  const canPerformActions = isBranchAdmin;

  const [tab, setTab] = useState('active');
  const [modal, setModal] = useState(false);
  
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // For members, we don't need tabs, just show active
  useEffect(() => {
    if (isMemberOnly) setTab('active');
  }, [isMemberOnly]);

  const load = async () => {
    setLoading(true);
    try {
      let allMembers = await getCollection('members');
      const allBranches = await getCollection('branches');
      setBranches(allBranches);

      if (user?.role === 'district_coordinator' && user?.districtId) {
        const districtBranches = allBranches.filter(b => b.districtId === user.districtId && b.status === 'active');
        const branchIds = districtBranches.map(b => b._id);
        allMembers = allMembers.filter(m => branchIds.includes(m.current_branch_id));
      } else if (isRestrictedToBranch && user?.branchId) {
        allMembers = allMembers.filter(m => m.current_branch_id === user.branchId);
      }

      setMembers(allMembers.filter(b => b.status === 'approved'));
      setPending(allMembers.filter(b => b.status === 'pending' || !b.status));

      const allDistricts = await getCollection('districts');
      setDistricts(allDistricts);

      const allFields = await getCollection('literary-fields');
      setLiteraryFields(allFields);

    } catch (err) { 
      console.error(err); 
      toast.error("Failed to load data.");
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { load(); }, []);

  const set = (path, value) => {
    setForm(prevForm => {
      const keys = path.split('.');
      const newForm = { ...prevForm };
      let current = newForm;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...(current[keys[i]] || {}) };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newForm;
    });
  };

  const toggleGenre = (fieldName) => {
    const current = form.literary_genre || [];
    const fieldToAdd = isRTL ? fieldName.nameSindhi : fieldName.name;
    if (current.includes(fieldToAdd)) {
      set('literary_genre', current.filter(name => name !== fieldToAdd));
    } else {
      set('literary_genre', [...current, fieldToAdd]);
    }
  };


  const openAdd = () => {
    const newForm = { ...EMPTY_MEMBER };
    if (isBranchAdmin) {
      newForm.current_branch_id = user?.branchId || '';
      newForm.districtId = user?.districtId || '';
    }
    setForm(newForm);
    setEditId(null);
    setModal(true);
  };

  const openEdit = m => {
    const memberData = {
      ...EMPTY_MEMBER,
      ...m,
      contact_info: { ...EMPTY_MEMBER.contact_info, ...(m.contact_info || {}) },
      addresses: { ...EMPTY_MEMBER.addresses, ...(m.addresses || {}) },
      dob: m.dob ? m.dob.split('T')[0] : '',
      join_date: m.join_date ? m.join_date.split('T')[0] : '',
    };
    setForm(memberData);
    setEditId(m._id);
    setModal(true);
  };

  const save = async e => {
    e.preventDefault();
    try {
      if (editId) {
        await updateDoc(doc(db, 'members', editId), form);
        toast.success(t('memberUpdated'));
      } else {
        const batch = writeBatch(db);
        const memberRef = doc(collection(db, 'members'));
        const userRef = doc(collection(db, 'users'));

        batch.set(memberRef, { ...form, status: 'pending', createdAt: new Date().toISOString(), userId: userRef.id });
        batch.set(userRef, {
          displayName: form.full_name,
          email: form.contact_info.email,
          role: 'member',
          branchId: form.current_branch_id,
          districtId: form.districtId,
          memberId: memberRef.id,
          createdAt: new Date().toISOString(),
        });

        await batch.commit();
        toast.success(t('memberCreated'));
      }
      setModal(false);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Error saving member');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, 'members', id), { status });
      toast.success(status === 'approved' ? t('approvedMsg') : t('rejectedMsg'));
      load();
    } catch { toast.error('Error'); }
  };

  const deleteMember = async id => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'members', id));
      toast.success(t('deletedMsg'));
      load();
    } catch { toast.error('Error'); }
  };

  const list = tab === 'active' ? members : pending;
  const renderName = (item) => isRTL ? (item.nameSindhi || item.name) : item.name;
  const renderFullName = (item) => isRTL ? (item.full_name_sindhi || item.full_name) : item.full_name;

  const getLiteraryFields = (m) => {
    const genres = m.literary_genre || [];
    const fields = m.literary_fields || [];
    const resolvedFields = fields.map(id => {
      const f = literaryFields.find(lf => lf._id === id);
      return f ? (isRTL ? f.nameSindhi : f.name) : null;
    }).filter(Boolean);
    return [...new Set([...genres, ...resolvedFields])];
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 42 : 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {isBranchAdmin ? t('manageMembers') : (isMemberOnly ? t('branchMembers') : t('members'))}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {members.length} {t('activeMembers')} {isBranchAdmin && `· ${pending.length} ${t('pendingRequests')}`}
          </p>
        </div>
        {canPerformActions && (
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn btn-primary" style={{ height: 48, padding: '0 24px', borderRadius: 12, fontSize: 14, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={openAdd}>
            {t('addMemberBtn')}
          </motion.button>
        )}
      </div>

      {isBranchAdmin && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, background: 'var(--sindhi-cream)', padding: 6, borderRadius: 16, width: 'fit-content', direction: isRTL ? 'rtl' : 'ltr' }}>
          {[
            ['active', t('approved')], 
            ['pending', t('pendingApproval')]
          ].map(([v, l]) => (
            <button key={v} className={`btn-tab ${tab === v ? 'active' : ''}`} style={{ padding: '10px 24px', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer', background: tab === v ? 'white' : 'transparent', color: tab === v ? 'var(--sindhi-navy)' : 'var(--sindhi-text-muted)', boxShadow: tab === v ? '0 4px 12px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setTab(v); setPage(1); }}>
              {l}
              {v === 'pending' && pending.length > 0 && <span style={{ background: 'var(--sindhi-maroon)', color: 'white', borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>{pending.length}</span>}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
      ) : (
        <Card style={{ overflow: 'hidden', border: '1px solid var(--sindhi-cream)', background: 'white' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)' }}>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('memberDetails')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('literaryFields')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('branches')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('status')}</th>
                  {canPerformActions && <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('actions')}</th>}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {list.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 80, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        {t('noMembersFound')}
                      </td>
                    </motion.tr>
                  ) : (
                    list.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((m) => {
                      const branch = branches.find(b => b._id === m.current_branch_id);
                      const district = districts.find(d => d._id === m.districtId);
                      return <motion.tr key={m._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ borderBottom: '1px solid var(--sindhi-cream)' }}>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontSize: 16, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{renderFullName(m)}</div>
                          <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)' }}>{m.contact_info?.phone || m.contact_info?.email}</div>
                          <div style={{ fontSize: 11, marginTop: 4, color: 'var(--sindhi-text-muted)' }}>{district ? renderName(district) : (m.districtId || '')}</div>
                        </td>
                        <td style={{ padding: '20px 24px', maxWidth: 300 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, direction: isRTL ? 'rtl' : 'ltr' }}>
                            {(() => {
                              const allFields = getLiteraryFields(m);
                              return allFields.length > 0 ? allFields.map((genre, i) => (
                                <span key={i} style={{ 
                                  padding: '4px 10px', 
                                  borderRadius: 6, 
                                  fontSize: 10, 
                                  fontWeight: 700, 
                                  background: 'var(--sindhi-ivory)', 
                                  color: 'var(--sindhi-navy)', 
                                  border: '1px solid var(--sindhi-cream)',
                                  fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {genre}
                                </span>
                              )) : <span style={{ color: 'var(--sindhi-text-muted)', fontSize: 12 }}>—</span>;
                            })()}
                          </div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontSize: 14, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', fontWeight: 600 }}>{branch ? renderName(branch) : '—'}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <span style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', background: m.status === 'approved' ? '#e1f9eb' : m.status === 'pending' ? '#fff5e6' : '#feecef', color: m.status === 'approved' ? '#1da352' : m.status === 'pending' ? '#d97706' : '#e11d48', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                            {t(m.status)}
                          </span>
                        </td>
                        {canPerformActions && (
                          <td style={{ padding: '20px 24px' }}>
                            <div style={{ display: 'flex', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                              {tab === 'pending' ? (
                                <>
                                  <button className="btn-action" style={{ background: '#1da352', color: 'white' }} onClick={() => updateStatus(m._id, 'approved')}>{t('approve')}</button>
                                  <button className="btn-action" style={{ background: '#ef4444', color: 'white' }} onClick={() => updateStatus(m._id, 'rejected')}>{t('reject')}</button>
                                </>
                              ) : (
                                <>
                                  <button className="btn-action" onClick={() => openEdit(m)}>{t('edit')}</button>
                                  <button className="btn-action" style={{ background: '#feecef', color: '#e11d48' }} onClick={() => deleteMember(m._id)}>{t('delete')}</button>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </motion.tr>;
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div style={{ padding: 24, direction: 'ltr' }}>
            <Pagination page={page} totalPages={Math.ceil(list.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        </Card>
      )}

      <AnimatePresence>
        {modal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="modal-box" style={{ width: 900, maxHeight: '90vh', overflowY: 'auto', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              <div style={{ padding: '24px 32px', background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 28 : 22, fontWeight: 800, color: 'var(--sindhi-navy)' }}>{editId ? t('edit') : t('register')} {t('member')}</h2>
                <button className="btn-close" onClick={() => setModal(false)}>✕</button>
              </div>
              <form onSubmit={save} style={{ padding: 32 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                  <div className="form-group">
                    <label className="form-label">{t('fullNameEnglish')} *</label>
                    <input className="form-input" value={form.full_name} onChange={e => set('full_name', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label sindhi-label">{t('fullNameSindhi')} *</label>
                    <input className="form-input sindhi-input" value={form.full_name_sindhi} onChange={e => set('full_name_sindhi', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('literaryName')}</label>
                    <input className="form-input" value={form.literary_name || ''} onChange={e => set('literary_name', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('dob')}</label>
                    <input className="form-input" type="date" value={form.dob} onChange={e => set('dob', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('phone')} *</label>
                    <input className="form-input" value={form.contact_info.phone} onChange={e => set('contact_info.phone', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('email')} *</label>
                    <input className="form-input" type="email" value={form.contact_info.email} onChange={e => set('contact_info.email', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('branches')} *</label>
                    <select className="form-input" value={form.current_branch_id} onChange={e => set('current_branch_id', e.target.value)} required disabled={isBranchAdmin}>
                      <option value="">{t('chooseBranchPlaceholder')}</option>
                      {branches.map(b => <option key={b._id} value={b._id}>{renderName(b)}</option>)}
                    </select>
                  </div>
                   <div className="form-group">
                    <label className="form-label">{t('districts')} *</label>
                    <select className="form-input" value={form.districtId} onChange={e => set('districtId', e.target.value)} required disabled={isBranchAdmin}>
                      <option value="">{t('selectDistrict')}</option>
                      {districts.map(d => <option key={d._id} value={d._id}>{renderName(d)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('membershipStatus')}</label>
                     <select className="form-input" value={form.membership_status} onChange={e => set('membership_status', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="Temporary">Temporary (Aarzi)</option>
                        <option value="Permanent">Permanent (Pakka)</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 3' }}>
                    <label className="form-label">{t('literaryServices')}</label>
                    <textarea className="form-input" value={form.literary_services || ''} onChange={e => set('literary_services', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t('temporaryAddress')}</label>
                    <input className="form-input" value={form.addresses.temporary} onChange={e => set('addresses.temporary', e.target.value)} />
                  </div>
                   <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t('permanentAddress')}</label>
                    <input className="form-input" value={form.addresses.permanent} onChange={e => set('addresses.permanent', e.target.value)} />
                  </div>
                </div>

                <div className="form-group" style={{ gridColumn: '1/-1', marginTop: 20, borderTop: '1px solid var(--sindhi-cream)', paddingTop: 20 }}>
                  <label className="form-label">{t('literaryFields')} ({t('optional')})</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginTop: 8 }}>
                    {literaryFields.map(f => (
                      <label key={f._id} className="checkbox-label">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          checked={form.literary_genre?.includes(isRTL ? f.nameSindhi : f.name)}
                          onChange={() => toggleGenre(f)}
                        />
                        <span className="sindhi-label">{isRTL ? f.nameSindhi : f.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 32, display: 'flex', gap: 12, borderTop: '1px solid var(--sindhi-cream)', paddingTop: 24, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button type="submit" className="btn btn-primary" style={{ height: 50, flex: 1 }}>
                    {editId ? t('save') : t('submit')}
                  </button>
                  <button type="button" onClick={() => setModal(false)} className="btn btn-outline" style={{ height: 50 }}>
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
